import React, { useEffect, useId } from 'react'
import PropTypes from 'prop-types'
import { NumericInput, FormGroup } from '@theneatcompany/nui'
import { useController, useFormContext } from 'react-hook-form'

import FieldError from './FieldError'

const AREA_CODE_LENGTH = 3

const PhoneNumberField = ({
  containerProps = {},
  disabled,
  errorProps = {},
  labelProps = {},
  label = labelProps.label,
  name,
  onBlur,
  shouldDisplayErrorMessage = true,
  helperMessage,
  helperProps = {},
  placeholder,
  ...inputProps
}) => {
  const id = useId()

  const { control, setValue, formState: { defaultValues, isSubmitting, isSubmitted } } = useFormContext()
  const {
    field: { value, onBlur: formOnBlur, onChange: formOnChange, ...field },
    fieldState: { error, isTouched: touched },
  } = useController({ name, control })
  const initialValue = defaultValues?.[name]

  const formatValue = (val) => {
    const digits = val.replace(/\D/g, '')
    let nextValue = digits
    const areaCode = `(${digits.slice(0, AREA_CODE_LENGTH)})`

    if (digits.length === AREA_CODE_LENGTH && value.length < AREA_CODE_LENGTH) {
      nextValue = areaCode
    }

    if (digits.length > AREA_CODE_LENGTH) {
      nextValue = areaCode
      if (digits.length > AREA_CODE_LENGTH) {
        nextValue += ` ${digits.slice(AREA_CODE_LENGTH, 6)}`
      }

      if (digits.length > 6) {
        nextValue += `-${digits.slice(6, 10)}`
      }
    }
    return nextValue
  }

  const setFormattedValue = val => setValue(name, formatValue(val))

  const handleBlur = (e) => {
    setFormattedValue(e.target.value)
    formOnBlur(e)

    onBlur?.(e)
  }

  useEffect(() => {
    if (initialValue) setFormattedValue(initialValue)
  }, [initialValue])

  const errorMessage = error?.message
  const hasErrors = errorProps.ignoreTouched || isSubmitted ? !!errorMessage : !!(errorMessage && touched)

  return (
    <FormGroup intent={hasErrors ? 'danger' : 'default'} {...containerProps}>
      <FormGroup.Label htmlFor={id} label={label} {...labelProps} />
      <NumericInput
        digitsOnly
        disabled={disabled || isSubmitting}
        id={id}
        name={name}
        onBlur={handleBlur}
        onValueChange={newValue => formOnChange(formatValue(newValue))}
        placeholder={placeholder || label}
        precision={0}
        value={value}
        {...inputProps}
        {...field}
      />
      <FieldError
        message={helperMessage || (shouldDisplayErrorMessage && errorMessage)}
        touched={touched}
        {...helperProps}
        {...errorProps}
      />
    </FormGroup>
  )
}

PhoneNumberField.propTypes = {
  containerProps: PropTypes.shape({}),
  disabled: PropTypes.bool,
  fieldOptions: PropTypes.shape({}),
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  labelProps: PropTypes.shape({
    label: PropTypes.node,
  }),
  placeholder: PropTypes.node,
  helperMessage: PropTypes.node,
  shouldDisplayErrorMessage: PropTypes.bool,
  onBlur: PropTypes.func,
  helperProps: PropTypes.shape({}),
  errorProps: PropTypes.shape({}),
}

export default PhoneNumberField
